import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './DadosComplementares.css';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const DadosComplementares = ({ clienteId }) => {
  const [cnpjInfo, setCnpjInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCNPJInfo = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('Token de autenticação não encontrado');
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/clientes/${clienteId}/cnpj-info`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setCnpjInfo(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error('Erro ao buscar informações complementares do CNPJ:', err);
        setError(err.response?.data?.message || err.message || 'Erro ao buscar informações complementares do CNPJ');
        setLoading(false);
      }
    };

    if (clienteId) {
      fetchCNPJInfo();
    }
  }, [clienteId]);

  if (loading) {
    return (
      <div className="loading-indicator">
        <div className="spinner"></div>
        <span>Carregando informações complementares do CNPJ...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-message">
        <i className="fas fa-exclamation-triangle"></i>
        Erro: {error}
      </div>
    );
  }
  
  if (!cnpjInfo) {
    return (
      <div className="info-message">
        <i className="fas fa-info-circle"></i>
        Nenhuma informação complementar de CNPJ disponível para este cliente.
      </div>
    );
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="dados-complementares-container">
      {/* Simples Nacional Information */}
      {cnpjInfo.company && (cnpjInfo.company.simples || cnpjInfo.company.simei) && (
        <div className="dados-complementares-section">
          <h3>
            <i className="fas fa-file-invoice-dollar"></i>
            Regimes Tributários
          </h3>
          <div className="dados-complementares-grid">
            {cnpjInfo.company.simples && (
              <>
                <div className="dados-complementares-item">
                  <span className="dados-complementares-label">Optante pelo Simples Nacional:</span>
                  <span className="dados-complementares-value">
                    {cnpjInfo.company.simples.optant ? 'Sim' : 'Não'}
                  </span>
                </div>
                {cnpjInfo.company.simples.since && (
                  <div className="dados-complementares-item">
                    <span className="dados-complementares-label">Optante desde:</span>
                    <span className="dados-complementares-value">
                      {formatDate(cnpjInfo.company.simples.since)}
                    </span>
                  </div>
                )}
              </>
            )}
            
            {cnpjInfo.company.simei && (
              <>
                <div className="dados-complementares-item">
                  <span className="dados-complementares-label">Optante pelo SIMEI:</span>
                  <span className="dados-complementares-value">
                    {cnpjInfo.company.simei.optant ? 'Sim' : 'Não'}
                  </span>
                </div>
                {cnpjInfo.company.simei.since && (
                  <div className="dados-complementares-item">
                    <span className="dados-complementares-label">Optante SIMEI desde:</span>
                    <span className="dados-complementares-value">
                      {formatDate(cnpjInfo.company.simei.since)}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Contact information (Phones & Emails) */}
      {((cnpjInfo.phones && cnpjInfo.phones.length > 0) || 
        (cnpjInfo.emails && cnpjInfo.emails.length > 0)) && (
        <div className="dados-complementares-section">
          <h3>
            <i className="fas fa-address-book"></i>
            Contatos
          </h3>
          
          {cnpjInfo.phones && cnpjInfo.phones.length > 0 && (
            <div className="dados-complementares-subsection">
              <h4>
                <i className="fas fa-phone"></i>
                Telefones
              </h4>
              <div className="dados-complementares-grid">
                {cnpjInfo.phones.map((phone, index) => (
                  <div key={index} className="dados-complementares-item">
                    <span className="dados-complementares-label">
                      {phone.type ? `${phone.type}:` : `Telefone ${index + 1}:`}
                    </span>
                    <span className="dados-complementares-value">
                      <i className="fas fa-phone contact-icon"></i>
                      {phone.area && `(${phone.area}) `}{phone.number}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {cnpjInfo.emails && cnpjInfo.emails.length > 0 && (
            <div className="dados-complementares-subsection">
              <h4>
                <i className="fas fa-envelope"></i>
                Emails
              </h4>
              <div className="dados-complementares-grid">
                {cnpjInfo.emails.map((email, index) => (
                  <div key={index} className="dados-complementares-item">
                    <span className="dados-complementares-label">
                      {email.ownership ? `${email.ownership}:` : `Email ${index + 1}:`}
                    </span>
                    <span className="dados-complementares-value">
                      <i className="fas fa-envelope contact-icon"></i>
                      {email.address}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Company Members / Partners */}
      {cnpjInfo.company && cnpjInfo.company.members && cnpjInfo.company.members.length > 0 && (
        <div className="dados-complementares-section">
          <h3>
            <i className="fas fa-users"></i>
            Sócios e Administradores
          </h3>
          <div className="dados-complementares-members-list">
            {cnpjInfo.company.members.map((member, index) => (
              <div key={index} className="dados-complementares-member-card">
                <div className="dados-complementares-member-header">
                  <h4>{member.person.name}</h4>
                  <span className="dados-complementares-member-role">{member.role.text}</span>
                </div>
                <div className="dados-complementares-member-details">
                  <div className="dados-complementares-item">
                    <span className="dados-complementares-label">Nome:</span>
                    <span className="dados-complementares-value">{member.person.name}</span>
                  </div>
                  <div className="dados-complementares-item">
                    <span className="dados-complementares-label">CPF/CNPJ:</span>
                    <span className="dados-complementares-value">{member.person.taxId}</span>
                  </div>
                  {member.since && (
                    <div className="dados-complementares-item">
                      <span className="dados-complementares-label">Desde:</span>
                      <span className="dados-complementares-value">{formatDate(member.since)}</span>
                    </div>
                  )}
                  {member.person.type && (
                    <div className="dados-complementares-item">
                      <span className="dados-complementares-label">Tipo:</span>
                      <span className="dados-complementares-value">
                        {member.person.type === 'NATURAL' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                      </span>
                    </div>
                  )}
                  {member.agent && member.agent.person && (
                    <div className="dados-complementares-agent-info">
                      <h5>Representante Legal</h5>
                      <div className="dados-complementares-item">
                        <span className="dados-complementares-label">Nome:</span>
                        <span className="dados-complementares-value">{member.agent.person.name}</span>
                      </div>
                      <div className="dados-complementares-item">
                        <span className="dados-complementares-label">CPF/CNPJ:</span>
                        <span className="dados-complementares-value">{member.agent.person.taxId}</span>
                      </div>
                      {member.agent.role && (
                        <div className="dados-complementares-item">
                          <span className="dados-complementares-label">Cargo:</span>
                          <span className="dados-complementares-value">{member.agent.role.text}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* State Registrations */}
      {cnpjInfo.registrations && cnpjInfo.registrations.length > 0 && (
        <div className="dados-complementares-section">
          <h3>
            <i className="fas fa-id-card-alt"></i>
            Inscrições Estaduais
          </h3>
          <div className="dados-complementares-registrations-table-wrapper">
            <table className="dados-complementares-registrations-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Estado</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Data do Status</th>
                </tr>
              </thead>
              <tbody>
                {cnpjInfo.registrations.map((reg, index) => (
                  <tr key={index}>
                    <td>{reg.number}</td>
                    <td>{reg.state}</td>
                    <td>{reg.type?.text || '-'}</td>
                    <td>
                      <span className={`status-badge ${reg.enabled ? 'status-ativo' : 'status-inativo'}`}>
                        {reg.status?.text || (reg.enabled ? 'Ativo' : 'Inativo')}
                      </span>
                    </td>
                    <td>{reg.statusDate ? formatDate(reg.statusDate) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Additional Information */}
      <div className="dados-complementares-section">
        <h3>
          <i className="fas fa-info-circle"></i>
          Informações Adicionais
        </h3>
        <div className="dados-complementares-grid">
          <div className="dados-complementares-item">
            <span className="dados-complementares-label">Matriz/Filial:</span>
            <span className="dados-complementares-value">
              {cnpjInfo.head !== undefined ? (cnpjInfo.head ? 'Matriz' : 'Filial') : 'Não informado'}
            </span>
          </div>
          
          {cnpjInfo.statusDate && (
            <div className="dados-complementares-item">
              <span className="dados-complementares-label">Data do Status:</span>
              <span className="dados-complementares-value">{formatDate(cnpjInfo.statusDate)}</span>
            </div>
          )}
          
          {cnpjInfo.suframa && cnpjInfo.suframa.length > 0 && (
            <div className="dados-complementares-item">
              <span className="dados-complementares-label">Inscrição SUFRAMA:</span>
              <span className="dados-complementares-value">
                {cnpjInfo.suframa.map((suf, i) => (
                  <div key={i}>{JSON.stringify(suf)}</div>
                ))}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DadosComplementares;